/**
 * Shoppable Video Stories — snippet-author runtime.
 *
 * Loaded async from each render of the snippet's main .liquid. Multi-execution
 * guarded — multiple instances on a page share one IIFE evaluation.
 *
 * Responsibilities:
 *   1. Read the per-instance pool block (sibling of <sai-kvb3hsy7>) for
 *      resolved video data.
 *   2. Upgrade preview placeholders with poster + label, or remove them
 *      when the video is missing from the pool (visible-degraded mode).
 *   3. Build the fullscreen <dialog> on first preview click and drive
 *      playback: auto-advance, swipe, progress bars, mute toggle, CTA.
 *
 * The snippet renders nothing client-side beyond what the pool resolves.
 *
 * Runtime variant rebuild contract:
 *   The bind() callback fires whenever targeting picks a different variant.
 *   applyVariant(content) re-applies every variant.content scalar — section
 *   header text, preview shape, lightbox settings (autoAdvance, mute,
 *   progress visibility, CTA text/action). The video pool
 *   does NOT swap per variant in v1 — the pool block is SSR'd once from
 *   the default-variant's `videos` metaobject-list, so non-default variants
 *   inherit the same tray + slide media. Variant changes to the `videos`
 *   prop are accepted into settings but not reflected in the rendered tray
 *   until a future SSR-all-pools (or fetch-on-resolve) implementation lands.
 *   An open lightbox closes on variant change so the next open uses the
 *   resolved scalar settings.
 */
;(() => {
  if (window.__sai_kvb3hsy7_initialized__) return
  window.__sai_kvb3hsy7_initialized__ = true

  const SNIPPET_ID = 'kvb3hsy7'
  // Pool key in the sibling <script data-spectrum-snippet-pool> JSON.
  // This is the snippet's private Liquid↔JS contract — independent of the
  // wrapper-level featureSlug declared in meta.json (which drives the slot
  // metafield lookup and the data-spectrum-feature-slug attribute).
  const POOL_KEY = 'shoppable_video_stories'
  // Analytics event prefix. Same featureSlug as the grid (svmk8tqx) by design —
  // both layouts roll up under one slug in PostHog. Layout-specific analysis
  // is achievable today via the `snippet_id` envelope key (kvb3hsy7 vs svmk8tqx).
  const FEATURE_SLUG = 'shoppable_videos'

  // Default no-op handles. Replaced by makePlayer.setAnalytics(track, emit)
  // once `__spectrumAi.snippet.bind(...)` returns its handles in
  // bindAllContainers. Until then, every event call is a silent no-op so
  // the snippet works in standalone contexts (local dev, tests).
  function noopTrack(_name, _payload) {}
  function noopEmit(_name, _detail) {}

  function clamp(n, lo, hi) {
    return Math.max(lo, Math.min(hi, n))
  }

  // How long a single image asset stays on screen before auto-advancing.
  // Matches the grid sibling (svmk8tqx) so both layouts feel identical when
  // a video metaobject mixes images and videos.
  const IMAGE_DISPLAY_MS = 5000

  // Normalize the per-video assets array. Falls back to a one-asset shim
  // when the parent-level video_url is present (transitional period while
  // older entries still ship parent fields rather than child assets).
  function getAssets(v) {
    if (Array.isArray(v?.assets) && v.assets.length > 0) {
      return v.assets.filter((a) => a?.url)
    }
    if (v?.video_url) {
      return [{ type: 'video', url: v.video_url, poster_url: v.poster_url || '' }]
    }
    return []
  }

  function readSnippetPool(outerContainer) {
    for (const child of outerContainer.children) {
      if (
        child.tagName === 'SCRIPT' &&
        child.getAttribute('type') === 'application/json' &&
        child.hasAttribute('data-spectrum-snippet-pool')
      ) {
        try {
          const parsed = JSON.parse(child.textContent || '{}')
          return parsed[POOL_KEY] || {}
        } catch {
          return {}
        }
      }
    }
    return {}
  }

  // ─── Preview tray upgrade ──────────────────────────────────────────────
  function upgradePreview(button, video) {
    button.classList.remove('sai-kvb3hsy7__preview--placeholder')
    const poster = button.querySelector('.sai-kvb3hsy7__preview-poster')
    const label = button.querySelector('.sai-kvb3hsy7__preview-label')
    if (poster && video.poster_url) {
      poster.style.backgroundImage = `url(${JSON.stringify(video.poster_url)})`
    }
    if (label && video.title) {
      // Full title in DOM; CSS handles visual truncation (white-space + ellipsis).
      label.textContent = video.title
      label.hidden = false
    }
    if (video.title) {
      button.setAttribute('aria-label', `Open story: ${video.title}`)
      button.setAttribute('title', video.title)
    }
  }

  // ─── Player ────────────────────────────────────────────────────────────
  function makePlayer(root, pool, settings) {
    const dialog = root.querySelector('.sai-kvb3hsy7__dialog')
    const slidesEl = root.querySelector('.sai-kvb3hsy7__slides')
    const progressEl = root.querySelector('.sai-kvb3hsy7__progress')
    const muteBtn = root.querySelector('.sai-kvb3hsy7__dialog-mute')
    const muteIconMuted = root.querySelector('.sai-kvb3hsy7__mute-icon--muted')
    const muteIconSound = root.querySelector('.sai-kvb3hsy7__mute-icon--sound')
    const navPrev = root.querySelector('.sai-kvb3hsy7__dialog-nav--prev')
    const navNext = root.querySelector('.sai-kvb3hsy7__dialog-nav--next')

    // A video belongs in the lightbox iff its asset list resolves to at least
    // one playable. Falls back to the legacy top-level video_url shim for
    // transitional pool data — handled by getAssets().
    const handles = Array.from(root.querySelectorAll('.sai-kvb3hsy7__preview'))
      .map((b) => b.dataset.handle)
      .filter((h) => getAssets(pool[h]).length > 0)

    const state = {
      index: 0,
      // Asset cursor inside the current slide. Reset to 0 on slide change,
      // or to (assetCount - 1) when navigating backwards into a slide.
      assetIndex: 0,
      muted: settings.muteOnLaunch,
      autoTimer: null,
      autoStartedAt: 0,
      autoElapsed: 0,
      // Listener bookkeeping so we can unbind the previous asset's `ended`
      // handler when the asset (or slide) changes.
      assetEndedHandler: null,
      assetEndedTarget: null,
    }

    // ─── Analytics state ───────────────────────────────────────────────
    let _track = noopTrack
    let _emit = noopEmit
    let _viewSession = null
    let _pendingExitReason
    let _impressedListFired = false
    const _impressedCards = new Set()
    let _impressedProducts = new Set()
    let _listObserver = null
    let _cardObserver = null
    let _productObserver = null
    let _scrollDebounceTimer = null
    let _lastTrayScrollLeft = 0

    function buildProductCardHtml(tag) {
      if (!tag) return ''
      const variants = Array.isArray(tag.variants) ? tag.variants : []
      const availableVariants = variants.filter((v) => v?.available)
      const productUnavailable = !tag.available || availableVariants.length === 0

      // Analytics provenance attributes — read by the dialog click delegate
      // (product_click), the per-product IO (product_impression), and the ATC
      // handler (add_to_cart). Tag id comes from tagged.system.id, product id
      // is the resolved Product GID — both surfaced by the Liquid pool block.
      const tagIdAttr = tag.tag_id != null ? ` data-tag-id="${tag.tag_id}"` : ''
      const productIdAttr = tag.id != null ? ` data-product-id="${tag.id}"` : ''

      // Build the action element based on cta_action + variant state.
      // Always uses cta_text label (matches Lotto convention) — except OOS which forces "Out of stock".
      // Redirect uses data-action="product-click" so it shares the analytics path with title/image clicks.
      let actionHtml
      if (settings.ctaAction === 'redirect') {
        actionHtml = `<a class="sai-kvb3hsy7__cta" href="${tag.url || '#'}" data-action="product-click">${settings.ctaText}</a>`
      } else if (productUnavailable) {
        actionHtml = `<button class="sai-kvb3hsy7__cta" type="button" disabled>Out of stock</button>`
      } else if (variants.length === 1) {
        // Single variant — direct ATC, no sheet.
        actionHtml = `<button class="sai-kvb3hsy7__cta" type="button" data-action="cta-atc-direct" data-variant-id="${variants[0].id}" data-product-handle="${tag.handle || ''}"${tagIdAttr}${productIdAttr}>${settings.ctaText}</button>`
      } else {
        // Multi-variant — opens the bottom sheet.
        actionHtml = `<button class="sai-kvb3hsy7__cta" type="button" data-action="cta-quickshop" data-product-handle="${tag.handle || ''}"${tagIdAttr}${productIdAttr}>${settings.ctaText}</button>`
      }

      const productHref = tag.url || '#'
      const titleHtml = `<a href="${productHref}" class="sai-kvb3hsy7__product-title" data-action="product-click">${tag.title || ''}</a>`
      const priceHtml = tag.price ? `<p class="sai-kvb3hsy7__product-price">${tag.price}</p>` : ''
      const imageHtml = tag.image_url
        ? `<a href="${productHref}" class="sai-kvb3hsy7__product-image-wrap" tabindex="-1" aria-hidden="true" data-action="product-click">
             <img class="sai-kvb3hsy7__product-image" src="${tag.image_url}" alt="" loading="lazy" width="60" height="60">
           </a>`
        : `<span class="sai-kvb3hsy7__product-image-wrap" aria-hidden="true"></span>`

      return `
        <div class="sai-kvb3hsy7__product-card" data-product-handle="${tag.handle || ''}"${tagIdAttr}${productIdAttr}>
          ${imageHtml}
          <div class="sai-kvb3hsy7__product-meta">
            ${titleHtml}
            ${priceHtml}
          </div>
          ${actionHtml}
        </div>
      `
    }

    function buildSlides() {
      slidesEl.innerHTML = handles
        .map((handle, i) => {
          const v = pool[handle]
          // Render every tagged product as its own card. Cards stack
          // horizontally inside an overflow-x: auto strip, mirroring the
          // grid sibling's pattern so a video with N tagged products
          // surfaces all of them — one tap target per product.
          const tags = Array.isArray(v.product_tags) ? v.product_tags : []
          const cardsHtml = tags.map(buildProductCardHtml).join('')
          const overlayHtml = cardsHtml
            ? `<div class="sai-kvb3hsy7__product-overlay"><div class="sai-kvb3hsy7__product-overlay-scroll">${cardsHtml}</div></div>`
            : ''

          // Each slide carries N asset slots (videos or images). Only one is
          // visible at a time — toggled via the `hidden` attribute. Asset
          // cycling within a slide drives the per-slide progress segments;
          // overflowing past the last asset advances the slide.
          //
          // <spectrum-video> uses data-load="click" so .load() must be called
          // explicitly when the asset activates. data-loop is set only for
          // single-asset slides — multi-asset slides rely on the inner
          // <video>'s `ended` event to advance to the next asset.
          const assets = getAssets(v)
          const single = assets.length <= 1
          const assetsHtml = assets
            .map((a, ai) => {
              const hidden = ai === 0 ? '' : ' hidden'
              if (a.type === 'image') {
                return `
                  <div class="sai-kvb3hsy7__asset" data-asset-idx="${ai}" data-asset-type="image"${hidden}>
                    <img class="sai-kvb3hsy7__asset-img" src="${a.url}" alt="" loading="lazy">
                  </div>
                `
              }
              const posterAttr = a.poster_url ? ` data-poster="${a.poster_url}"` : ''
              const posterImg = a.poster_url
                ? `<img src="${a.poster_url}" alt="" loading="lazy" style="width:100%;height:100%;object-fit:cover;display:block;">`
                : ''
              const loopAttr = single ? ' data-loop' : ''
              return `
                <div class="sai-kvb3hsy7__asset" data-asset-idx="${ai}" data-asset-type="video"${hidden}>
                  <spectrum-video
                    class="sai-kvb3hsy7__slide-video"
                    data-src="${a.url}"${posterAttr}
                    data-autoplay="muted"${loopAttr}
                    data-load="click"
                  >${posterImg}</spectrum-video>
                </div>
              `
            })
            .join('')

          return `
            <div class="sai-kvb3hsy7__slide" data-index="${i}" data-handle="${handle}" data-asset-count="${assets.length}" data-active-asset="0">
              <div class="sai-kvb3hsy7__slide-frame">
                ${assetsHtml}
                ${overlayHtml}
              </div>
            </div>
          `
        })
        .join('')
    }

    // Per-slide progress bar — one segment per asset in the CURRENT slide.
    // Rebuilt on every slide change because asset counts vary by video.
    // (Older behavior was one segment per video for the whole tray; that
    // global indicator is dropped in favor of the IG-style in-story bar.)
    function buildProgress(slideIdx) {
      if (!progressEl) return
      const slide = slidesEl?.children[slideIdx]
      const count = slide ? Number.parseInt(slide.dataset.assetCount || '1', 10) : 1
      let html = ''
      for (let i = 0; i < count; i++) {
        html += `<span class="sai-kvb3hsy7__progress-segment"><span class="sai-kvb3hsy7__progress-fill"></span></span>`
      }
      progressEl.innerHTML = html
    }

    // Active slide / asset accessors. Asset cursor lives in state.assetIndex;
    // each slide stores its asset count in data-asset-count so the player
    // doesn't have to look up the pool again at runtime.
    function getActiveSlide() {
      return slidesEl?.children[state.index] || null
    }

    function getActiveAssetEl() {
      const slide = getActiveSlide()
      if (!slide) return null
      const assets = slide.querySelectorAll('.sai-kvb3hsy7__asset')
      return assets[state.assetIndex] || null
    }

    function getActiveAssetType() {
      return getActiveAssetEl()?.dataset?.assetType || 'video'
    }

    // The active <spectrum-video> custom element — exposes play/pause/mute/unmute.
    // Returns null when the active asset is an image.
    function getActiveVideo() {
      const el = getActiveAssetEl()
      if (!el || el.dataset.assetType !== 'video') return null
      return el.querySelector('spectrum-video') || null
    }

    // Inner native <video> — for currentTime / duration / `ended` event hooks.
    function getActiveInnerVideo() {
      return getActiveVideo()?.querySelector('video') || null
    }

    function setMuted(muted) {
      state.muted = muted
      const v = getActiveVideo()
      if (v) {
        if (muted) v.mute?.()
        else v.unmute?.()
      }
      // Use inline style.display rather than the [hidden] attribute — some
      // user agents apply baseline svg display rules that win over [hidden],
      // matching the existing svmk8tqx pattern.
      if (muteIconMuted) muteIconMuted.style.display = muted ? '' : 'none'
      if (muteIconSound) muteIconSound.style.display = muted ? 'none' : ''
      if (muteBtn) muteBtn.setAttribute('aria-label', muted ? 'Unmute video' : 'Mute video')
    }

    // One timer drives both the progress-bar pulse and the auto-advance —
    // we never want to leave it running across asset / slide transitions.
    function clearAssetTimer() {
      if (state.autoTimer) {
        clearTimeout(state.autoTimer)
        state.autoTimer = null
      }
      if (state.assetEndedTarget && state.assetEndedHandler) {
        state.assetEndedTarget.removeEventListener('ended', state.assetEndedHandler)
      }
      state.assetEndedTarget = null
      state.assetEndedHandler = null
    }

    // Back-compat alias — older call sites used clearAutoTimer.
    const clearAutoTimer = clearAssetTimer

    function setProgressFill(i, percent) {
      if (!progressEl) return
      const segment = progressEl.children[i]
      if (!segment) return
      const fill = segment.firstElementChild
      if (fill) fill.style.width = `${clamp(percent, 0, 100)}%`
    }

    // Snap segments to a steady state relative to the current asset cursor:
    // earlier segments are filled (already watched within this slide),
    // current is 0% (about to fill), later are empty.
    function resetProgress(beforeIdx) {
      if (!progressEl) return
      for (let i = 0; i < progressEl.children.length; i++) {
        if (i < beforeIdx) setProgressFill(i, 100)
        else setProgressFill(i, 0)
      }
    }

    // Activate the asset at (state.index, state.assetIndex). Toggles hidden,
    // wires up the type-specific advance trigger (video `ended` event vs
    // image timeout), and starts the progress pulse for its segment.
    function startAssetPlayback() {
      clearAssetTimer()
      const slide = getActiveSlide()
      if (!slide) return
      const assets = slide.querySelectorAll('.sai-kvb3hsy7__asset')
      for (let i = 0; i < assets.length; i++) {
        if (i === state.assetIndex) assets[i].removeAttribute('hidden')
        else {
          assets[i].setAttribute('hidden', '')
          // Pause neighbor videos so they don't keep audio alive.
          const sv = assets[i].querySelector('spectrum-video')
          if (sv) sv.pause?.()
        }
      }

      const assetEl = getActiveAssetEl()
      if (!assetEl) return
      const assetType = assetEl.dataset.assetType
      slide.dataset.activeAsset = String(state.assetIndex)
      resetProgress(state.assetIndex)

      state.autoStartedAt = Date.now()
      state.autoElapsed = 0
      const startedSlide = state.index
      const startedAsset = state.assetIndex

      if (assetType === 'image') {
        // Fixed display window for images.
        const tick = () => {
          if (state.index !== startedSlide || state.assetIndex !== startedAsset) return
          const elapsed = Date.now() - state.autoStartedAt
          const pct = Math.min(100, (elapsed / IMAGE_DISPLAY_MS) * 100)
          setProgressFill(state.assetIndex, pct)
          if (elapsed >= IMAGE_DISPLAY_MS) {
            clearAssetTimer()
            goAsset(+1)
          } else {
            state.autoTimer = setTimeout(tick, 60)
          }
        }
        tick()
        return
      }

      // Video asset — load + play, then drive progress from currentTime /
      // duration. Hook `ended` on the inner <video> so we advance the asset
      // as soon as playback completes (more accurate than polling).
      const sv = assetEl.querySelector('spectrum-video')
      if (sv) {
        sv.load?.()
        if (state.muted) sv.mute?.()
        else sv.unmute?.()
        sv.play?.()
      }
      const inner = sv?.querySelector('video') || null
      if (inner) {
        const onEnded = () => {
          if (state.index !== startedSlide || state.assetIndex !== startedAsset) return
          clearAssetTimer()
          goAsset(+1)
        }
        inner.addEventListener('ended', onEnded)
        state.assetEndedTarget = inner
        state.assetEndedHandler = onEnded
      }
      const tick = () => {
        if (state.index !== startedSlide || state.assetIndex !== startedAsset) return
        const dur = inner?.duration
        const useVideoTime = inner && Number.isFinite(dur) && dur > 0
        const fallbackMs = settings.autoAdvanceSeconds * 1000
        const pct = useVideoTime
          ? Math.min(100, (inner.currentTime / dur) * 100)
          : Math.min(100, ((Date.now() - state.autoStartedAt) / fallbackMs) * 100)
        setProgressFill(state.assetIndex, pct)
        // `ended` is the authoritative advance trigger for videos. The
        // duration-poll fallback only handles assets whose metadata never
        // loads.
        const fallbackDone = !useVideoTime && Date.now() - state.autoStartedAt >= fallbackMs
        if (fallbackDone) {
          clearAssetTimer()
          goAsset(+1)
        } else {
          state.autoTimer = setTimeout(tick, 60)
        }
      }
      tick()
    }

    // Cycle the asset cursor inside the current slide. Overflow advances or
    // retreats the slide and lands on the appropriate boundary asset (first
    // asset on forward overflow, last asset on backward overflow).
    function goAsset(delta) {
      const slide = getActiveSlide()
      if (!slide) return
      const count = Number.parseInt(slide.dataset.assetCount || '1', 10)
      const next = state.assetIndex + delta
      if (next >= count) {
        go(state.index + 1, 0)
        return
      }
      if (next < 0) {
        go(state.index - 1, -1)
        return
      }
      state.assetIndex = next
      startAssetPlayback()
    }

    // Back-compat for the auto-advance call site inside go() — older flows
    // referenced startAutoAdvance directly. Single-asset slides keep the
    // same auto-advance ergonomics they had pre-multi-asset.
    function startAutoAdvance() {
      startAssetPlayback()
    }

    // Slide navigation. `startAsset` controls which asset within the new
    // slide we land on:
    //   • 0   — first asset (the default, e.g. forward auto-advance overflow)
    //   • -1  — last asset (backward overflow from asset 0 of a multi-asset slide)
    function go(targetIndex, startAsset) {
      const total = handles.length
      if (total === 0) return

      // Past the last story → either close (default per spec) or loop the
      // last story (when stories_close_on_last is false).
      let nextIndex = targetIndex
      if (nextIndex >= total) {
        if (settings.closeOnLast) {
          close()
          return
        }
        // Loop: stay on the last story and replay it from start.
        nextIndex = total - 1
      }
      if (nextIndex < 0) nextIndex = 0
      const previousIndex = state.index
      const indexChanged = nextIndex !== previousIndex
      state.index = nextIndex

      // Tear down the previous asset's playback before touching DOM so any
      // pending tick / `ended` listener can't fire against the new state.
      clearAssetTimer()

      // Pause + reset every video across every slide. Multi-asset slides
      // expose more than one <spectrum-video> per slide, so the previous
      // single-querySelector pattern would miss neighbors.
      for (let i = 0; i < slidesEl.children.length; i++) {
        const vids = slidesEl.children[i].querySelectorAll('spectrum-video')
        for (const sv of vids) {
          sv.pause?.()
          const inner = sv.querySelector('video')
          if (inner) inner.currentTime = 0
        }
      }

      slidesEl.style.transform = `translateX(${-100 * state.index}%)`
      slidesEl.style.transition = 'transform 240ms ease'

      // Slides use data-load="click", so the active slide (and its immediate
      // neighbors, for swipe responsiveness) need an explicit .load() before
      // playback — IntersectionObserver-based loading is unreliable inside a
      // modal <dialog> because of its top-layer stacking context.
      for (const offset of [-1, 0, 1]) {
        const idx = state.index + offset
        if (idx < 0 || idx >= total) continue
        const nVids = slidesEl.children[idx]?.querySelectorAll('spectrum-video') ?? []
        for (const sv of nVids) sv.load?.()
      }

      // Resolve the asset cursor for the new slide. -1 means "last asset"
      // (backward overflow case); anything else (including undefined) starts
      // at 0.
      const newSlide = slidesEl.children[state.index]
      const newCount = newSlide ? Number.parseInt(newSlide.dataset.assetCount || '1', 10) : 1
      state.assetIndex = startAsset === -1 ? Math.max(0, newCount - 1) : 0

      // Rebuild the progress bar for the new slide's asset count, then kick
      // off the active asset's playback.
      buildProgress(state.index)

      // Toggle nav buttons (desktop). Multi-asset slides keep the prev/next
      // buttons enabled at story boundaries so they can step through assets.
      if (navPrev) navPrev.hidden = state.index === 0 && state.assetIndex === 0
      if (navNext) navNext.hidden = state.index === total - 1 && state.assetIndex === newCount - 1

      startAssetPlayback()

      // Analytics: when the active slide actually changes mid-dialog, fire
      // view_duration for the outgoing video and view for the incoming one.
      // Asset-level cycling within the SAME slide does not fire view —
      // analytics granularity matches the grid sibling (one view per
      // spectrum_shoppable_video metaobject, not per asset).
      if (indexChanged && _viewSession) {
        const exitReason = nextIndex > previousIndex ? 'next' : 'prev'
        _fireView(nextIndex, exitReason)
        _setupProductImpressionsObserver()
      }
    }

    // ─── Analytics helpers ─────────────────────────────────────────────
    // All hooks mirror the grid (svmk8tqx) — same event names, same payload
    // shapes, same fire conditions. See svmk8tqx/__tests__/analytics.test.ts
    // for the canonical definitions.

    function _activeVideoHandle() {
      return slidesEl?.children[state.index]?.dataset?.handle
    }

    function _fireView(index, exitReason) {
      const slide = slidesEl?.children[index]
      const handle = slide?.dataset?.handle
      if (!handle) return
      // Pair an outgoing view_duration with the incoming view, keyed by exit_reason.
      if (_viewSession && exitReason) _fireViewDuration(exitReason)
      // Listen on the inner <video> — spectrum-video doesn't dispatch a wrapped
      // 'ended' event, but the inner element does (events bubble through the
      // custom element's shadow boundary normally for HTMLMediaElement events).
      const innerVideo = slide.querySelector('spectrum-video')?.querySelector('video') || null
      const session = {
        videoId: handle,
        startedAt: Date.now(),
        looped: false,
        video: innerVideo,
        onEnded: null,
      }
      if (innerVideo) {
        session.onEnded = () => {
          session.looped = true
        }
        innerVideo.addEventListener('ended', session.onEnded)
      }
      _viewSession = session
      _track(`${FEATURE_SLUG}:view_impression`, {
        video_id: handle,
        entity_id: handle,
        entity_type: 'video',
      })
    }

    function _fireViewDuration(exitReason) {
      const session = _viewSession
      if (!session) return
      if (session.video && session.onEnded) {
        session.video.removeEventListener('ended', session.onEnded)
      }
      const payload = {
        video_id: session.videoId,
        entity_id: session.videoId,
        entity_type: 'video',
        duration_ms: Date.now() - session.startedAt,
        loop: session.looped === true,
        exit_reason: exitReason,
      }
      _viewSession = null
      _track(`${FEATURE_SLUG}:view_duration`, payload)
    }

    function _setupListImpressionObserver() {
      if (_impressedListFired || _listObserver) return
      if (typeof IntersectionObserver === 'undefined') return
      _listObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting || entry.intersectionRatio < 0.5) continue
            if (_impressedListFired) continue
            _impressedListFired = true
            // Count cards currently visible (≥50% in viewport) via bounding-rect.
            let visible = 0
            for (const card of root.querySelectorAll('.sai-kvb3hsy7__preview')) {
              const r = card.getBoundingClientRect()
              if (r.width === 0 || r.height === 0) continue
              const visX = Math.max(
                0,
                Math.min(r.right, window.innerWidth || 0) - Math.max(r.left, 0),
              )
              const visY = Math.max(
                0,
                Math.min(r.bottom, window.innerHeight || 0) - Math.max(r.top, 0),
              )
              if ((visX * visY) / (r.width * r.height) >= 0.5) visible += 1
            }
            _track(`${FEATURE_SLUG}:list_impression`, {
              list_layout: 'stories',
              total_videos_in_list: handles.length,
              visible_video_count: visible,
            })
            _listObserver?.disconnect()
            _listObserver = null
          }
        },
        { threshold: 0.5 },
      )
      _listObserver.observe(root)
    }

    function _setupCardImpressionsObserver() {
      if (typeof IntersectionObserver === 'undefined') return
      _cardObserver?.disconnect()
      _cardObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting || entry.intersectionRatio < 0.5) continue
            const card = entry.target
            const handle = card.dataset?.handle
            if (!handle || _impressedCards.has(handle)) continue
            _impressedCards.add(handle)
            const v = pool[handle]
            const taggedCount = Array.isArray(v?.product_tags) ? v.product_tags.length : 0
            _track(`${FEATURE_SLUG}:preview_impression`, {
              video_id: handle,
              entity_id: handle,
              entity_type: 'video',
              tagged_product_count: taggedCount,
            })
            _cardObserver?.unobserve(card)
          }
        },
        { threshold: 0.5 },
      )
      for (const card of root.querySelectorAll('.sai-kvb3hsy7__preview')) {
        if (!_impressedCards.has(card.dataset?.handle || '')) _cardObserver.observe(card)
      }
    }

    function _setupProductImpressionsObserver() {
      _productObserver?.disconnect()
      _productObserver = null
      if (typeof IntersectionObserver === 'undefined') return
      const slide = slidesEl?.children[state.index]
      const card = slide?.querySelector('.sai-kvb3hsy7__product-card')
      if (!card) return
      const videoId = slide.dataset?.handle
      const tagId = card.dataset?.tagId
      const productId = card.dataset?.productId
      const key = `${videoId}|${tagId}`
      if (_impressedProducts.has(key)) return
      _productObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting || entry.intersectionRatio < 0.5) continue
            if (_impressedProducts.has(key)) continue
            _impressedProducts.add(key)
            _track(`${FEATURE_SLUG}:product_impression`, {
              video_id: videoId,
              entity_id: videoId || '',
              entity_type: 'video',
              tag_id: tagId,
              product_id: productId,
            })
            _productObserver?.unobserve(entry.target)
          }
        },
        { threshold: 0.5 },
      )
      _productObserver.observe(card)
    }

    function _onTrayScroll() {
      if (_scrollDebounceTimer) clearTimeout(_scrollDebounceTimer)
      _scrollDebounceTimer = setTimeout(() => {
        const tray = root.querySelector('.sai-kvb3hsy7__tray')
        if (!tray) return
        const current = tray.scrollLeft
        const delta = current - _lastTrayScrollLeft
        _lastTrayScrollLeft = current
        if (delta === 0) return
        _track(`${FEATURE_SLUG}:list_scroll_elem_clicked`, {
          scroll_direction: delta > 0 ? 'forward' : 'backward',
        })
      }, 250)
    }

    function _fireProductClick(actionEl) {
      // Provenance can come from the wrapping product card (slide overlay) or
      // from the action element itself (mini-PDP "View product" link, which
      // has no card ancestor — the JS stamps tag/product ids on it directly).
      const card = actionEl.closest('.sai-kvb3hsy7__product-card')
      const source = card || actionEl
      const inner = getActiveInnerVideo()
      _track(`${FEATURE_SLUG}:product_click`, {
        video_id: _activeVideoHandle(),
        entity_id: _activeVideoHandle() || '',
        entity_type: 'video',
        tag_id: source.dataset?.tagId,
        product_id: source.dataset?.productId,
        video_progress_seconds: inner?.currentTime ?? 0,
      })
    }

    function setAnalytics(trackFn, emitFn) {
      _track = typeof trackFn === 'function' ? trackFn : noopTrack
      _emit = typeof emitFn === 'function' ? emitFn : noopEmit
      _setupListImpressionObserver()
      _setupCardImpressionsObserver()
    }

    // Re-apply every variant-scoped scalar from the resolved variant.content.
    // The `videos` prop is intentionally NOT re-applied here — the pool is
    // SSR'd once from the default variant's video list, and runtime swapping
    // requires either pre-resolving all variants' pools or fetching on demand.
    // An open lightbox is closed so the next open() rebuilds with the new
    // settings, avoiding mid-watch structural patches.
    function applyVariant(content) {
      if (!content || typeof content !== 'object') return

      if (content.auto_advance_seconds != null) {
        settings.autoAdvanceSeconds = clamp(
          Number.parseInt(content.auto_advance_seconds, 10) || 5,
          1,
          30,
        )
      }
      if (typeof content.mute_on_launch === 'boolean') {
        settings.muteOnLaunch = content.mute_on_launch
      }
      if (typeof content.show_progress_bar === 'boolean') {
        settings.showProgressBar = content.show_progress_bar
      }
      if (typeof content.stories_close_on_last === 'boolean') {
        settings.closeOnLast = content.stories_close_on_last
      }
      if (typeof content.cta_text === 'string' && content.cta_text) {
        settings.ctaText = content.cta_text
      }
      if (typeof content.cta_action === 'string') {
        settings.ctaAction = content.cta_action === 'mini_pdp' ? 'mini_pdp' : 'redirect'
      }

      const headerEl = root.querySelector('.sai-kvb3hsy7__header')
      const titleEl = root.querySelector('.sai-kvb3hsy7__title')
      const descEl = root.querySelector('.sai-kvb3hsy7__description')
      if (titleEl && typeof content.section_title === 'string') {
        titleEl.textContent = content.section_title
        titleEl.toggleAttribute('hidden', content.section_title === '')
      }
      if (descEl && typeof content.section_description === 'string') {
        descEl.textContent = content.section_description
        descEl.toggleAttribute('hidden', content.section_description === '')
      }
      if (headerEl) {
        const titleHidden = titleEl ? titleEl.hasAttribute('hidden') : true
        const descHidden = descEl ? descEl.hasAttribute('hidden') : true
        headerEl.toggleAttribute('hidden', titleHidden && descHidden)
      }

      if (typeof content.preview_shape === 'string') {
        const shape = ['circle', 'square', 'rounded'].includes(content.preview_shape)
          ? content.preview_shape
          : 'circle'
        root.classList.remove(
          'sai-kvb3hsy7--shape-circle',
          'sai-kvb3hsy7--shape-square',
          'sai-kvb3hsy7--shape-rounded',
        )
        root.classList.add(`sai-kvb3hsy7--shape-${shape}`)
      }

      if (typeof content.show_preview_play_button === 'boolean') {
        for (const el of root.querySelectorAll('.sai-kvb3hsy7__preview-play')) {
          el.toggleAttribute('hidden', !content.show_preview_play_button)
        }
      }

      if (progressEl) {
        progressEl.toggleAttribute('hidden', !settings.showProgressBar)
      }

      if (dialog.open) close()
    }

    function open(startIndex) {
      buildSlides()
      // Pre-position the track on the starting slide; go() will rebuild the
      // progress bar for that slide's asset count and kick off playback.
      slidesEl.style.transform = `translateX(${-100 * startIndex}%)`
      setMuted(settings.muteOnLaunch)
      // Reset per-dialog product-impression dedup. Each dialog session is
      // independent — re-opening re-fires product impressions.
      _impressedProducts = new Set()
      if (typeof dialog.showModal === 'function') {
        dialog.showModal()
      } else {
        dialog.setAttribute('open', '')
      }
      go(startIndex, 0)
      // Fire `view` for the initial active slide (no exit_reason — first view).
      _fireView(state.index, null)
      _setupProductImpressionsObserver()
    }

    function close() {
      // Fire view_duration for the active video before tearing down. The
      // `_pendingExitReason` flag is set by pagehide; any other call is 'close'.
      const exitReason = _pendingExitReason ?? 'close'
      _fireViewDuration(exitReason)
      _pendingExitReason = undefined
      _productObserver?.disconnect()
      _productObserver = null
      clearAssetTimer()
      // Pause every video across every asset in every slide — multi-asset
      // slides expose more than one <spectrum-video> per slide.
      for (const sv of slidesEl.querySelectorAll('spectrum-video')) sv.pause?.()
      closeQuickshop()
      if (typeof dialog.close === 'function') dialog.close()
      else dialog.removeAttribute('open')
      // <dialog>.close() returns focus to the originally focused trigger using
      // :focus-visible semantics — that paints a UA outline around the preview
      // tile we came from. Drop focus to the body so the tray returns clean.
      document.activeElement?.blur?.()
    }

    // ─── Quickshop bottom sheet ─────────────────────────────────────────
    const quickshopEl = root.querySelector('.sai-kvb3hsy7__quickshop')
    // Per-attribute selection lives in `optionValues` keyed by option name —
    // single source of truth for variant resolution + UI render.
    const qsState = {
      variantId: '',
      handle: '',
      tag: null,
      optionValues: {},
      hasOptions: false,
    }
    let qsCloseTimer = null

    // Resolve which tagged product the user picked. With multi-product
    // strips, the cta-quickshop button carries data-tag-id (set by
    // buildProductCardHtml) — match against that. Falls back to product
    // handle, then to the first tag for legacy single-card slides.
    function getQsTagFromAction(actionEl) {
      const slide = actionEl?.closest?.('.sai-kvb3hsy7__slide')
      if (!slide) return null
      const v = pool[slide.dataset.handle]
      const tags = Array.isArray(v?.product_tags) ? v.product_tags : []
      if (tags.length === 0) return null
      const tagId = actionEl.dataset?.tagId
      if (tagId) {
        const hit = tags.find((t) => String(t?.tag_id) === String(tagId))
        if (hit) return hit
      }
      const handle = actionEl.dataset?.productHandle
      if (handle) {
        const hit = tags.find((t) => t?.handle === handle)
        if (hit) return hit
      }
      return tags[0]
    }

    // Match a variant whose options[] aligns with the selected values for the
    // tag's options[] (positional). Returns the variant or null.
    function findVariantByOptions(tag, optionValues) {
      const variants = Array.isArray(tag.variants) ? tag.variants : []
      const opts = Array.isArray(tag.options) ? tag.options : []
      for (const v of variants) {
        const vOpts = Array.isArray(v.options) ? v.options : []
        let hit = true
        for (let i = 0; i < opts.length; i++) {
          if (vOpts[i] !== optionValues[opts[i].name]) {
            hit = false
            break
          }
        }
        if (hit) return v
      }
      return null
    }

    // Pick the first available variant; fall back to the first variant. Used
    // to seed pre-selection when the snippet doesn't carry a pre_selected_variant.
    function pickInitialVariant(tag) {
      const variants = Array.isArray(tag.variants) ? tag.variants : []
      if (tag.first_variant_id) {
        const target = variants.find((v) => String(v.id) === String(tag.first_variant_id))
        if (target) return target
      }
      return variants.find((v) => v?.available) || variants[0] || null
    }

    // Build a pre-selection map (name → value) from a chosen variant's
    // options[] aligned with the tag's options[]. When the variant doesn't
    // map to an option position the value is empty — pill render still works.
    function optionValuesFromVariant(tag, variant) {
      const result = {}
      const opts = Array.isArray(tag.options) ? tag.options : []
      const vOpts = Array.isArray(variant?.options) ? variant.options : []
      opts.forEach((opt, i) => {
        result[opt.name] = vOpts[i] ?? ''
      })
      return result
    }

    // Is there any variant available with `optName=optValue` AND every other
    // currently-selected option held fixed? Used to disable invalid combos.
    function isOptionValueAvailable(tag, optName, optValue, currentValues) {
      const variants = Array.isArray(tag.variants) ? tag.variants : []
      const opts = Array.isArray(tag.options) ? tag.options : []
      for (const v of variants) {
        if (!v.available) continue
        const vOpts = Array.isArray(v.options) ? v.options : []
        let hit = true
        for (let i = 0; i < opts.length; i++) {
          const name = opts[i].name
          const target = name === optName ? optValue : currentValues[name]
          if (target != null && target !== '' && vOpts[i] !== target) {
            hit = false
            break
          }
        }
        if (hit) return true
      }
      return false
    }

    function buildPill(opt, ov, tag) {
      const btn = document.createElement('button')
      btn.type = 'button'
      btn.className = 'sai-kvb3hsy7__quickshop-pill'
      btn.setAttribute('role', 'radio')
      btn.dataset.optionName = opt.name
      btn.dataset.optionValue = ov.name
      const isSwatchColor = !!ov.swatch_color
      const isSwatchImage = !!ov.swatch_image
      if (isSwatchColor) btn.classList.add('sai-kvb3hsy7__quickshop-pill--swatch-color')
      if (isSwatchImage) btn.classList.add('sai-kvb3hsy7__quickshop-pill--swatch-image')

      let inner = ''
      if (isSwatchImage) {
        inner += `<span class="sai-kvb3hsy7__quickshop-pill-swatch" style="background-image:url('${ov.swatch_image}')"></span>`
      } else if (isSwatchColor) {
        inner += `<span class="sai-kvb3hsy7__quickshop-pill-swatch" style="background-color:${ov.swatch_color}"></span>`
      }
      inner += `<span class="sai-kvb3hsy7__quickshop-pill-label">${ov.name}</span>`
      btn.innerHTML = inner
      return btn
    }

    // Re-paint the pill grid + header (image/price/compare/atc) based on the
    // current qsState. Called on populate AND on every pill click.
    function applySelection(tag) {
      if (!tag) return
      // Find matching variant + bind atc
      const variant = findVariantByOptions(tag, qsState.optionValues)
      qsState.variantId = variant ? String(variant.id) : ''

      // Repaint each pill — selected/disabled state.
      const groups = quickshopEl.querySelectorAll('[data-qs-pills]')
      for (const grp of groups) {
        const optName = grp.dataset.qsPills
        const pills = grp.querySelectorAll('.sai-kvb3hsy7__quickshop-pill')
        for (const pill of pills) {
          const value = pill.dataset.optionValue
          const selected = qsState.optionValues[optName] === value
          pill.classList.toggle('sai-kvb3hsy7__quickshop-pill--selected', selected)
          pill.setAttribute('aria-checked', selected ? 'true' : 'false')
          const available = isOptionValueAvailable(tag, optName, value, qsState.optionValues)
          pill.classList.toggle('sai-kvb3hsy7__quickshop-pill--oos', !available)
          if (!available) {
            pill.setAttribute('aria-label', `${value} — out of stock`)
            pill.setAttribute('aria-disabled', 'true')
          } else {
            pill.removeAttribute('aria-label')
            pill.removeAttribute('aria-disabled')
          }
        }
        // Selected-value label next to the option name.
        const selectedLabel = grp.parentElement.querySelector('[data-qs-selected]')
        if (selectedLabel) selectedLabel.textContent = qsState.optionValues[optName] || ''
      }

      // Header: thumbnail prefers the matched variant's featured_image, else
      // the tag-level image. Lets shoppers see the variant they're inspecting.
      const img = quickshopEl.querySelector('[data-qs-image]')
      if (img) {
        const v_img = variant?.featured_image
        img.src = v_img && v_img !== '' ? v_img : tag.image_url || ''
        img.alt = tag.title || ''
      }

      // Price: variant price wins over tag-level when present.
      const price = quickshopEl.querySelector('[data-qs-price]')
      if (price) price.textContent = (variant?.price ?? tag.price) || ''

      // Compare-at: hide unless we have a tag-level value (variant compare not
      // surfaced today; can be added when needed).
      const compare = quickshopEl.querySelector('[data-qs-compare]')
      if (compare) {
        if (tag.compare_at_price) {
          compare.textContent = tag.compare_at_price
          compare.hidden = false
        } else {
          compare.textContent = ''
          compare.hidden = true
        }
      }

      // ATC: enabled only when a valid + available variant is selected.
      const atc = quickshopEl.querySelector('[data-action="qs-atc"]')
      if (atc) atc.disabled = !qsState.variantId || !variant?.available
    }

    function populateQuickshop(tag) {
      if (!quickshopEl || !tag) return
      qsState.tag = tag
      qsState.handle = tag.handle || ''

      // Static header bits — title text + view-product link don't depend on
      // the selected variant.
      const title = quickshopEl.querySelector('[data-qs-title]')
      if (title) {
        title.textContent = tag.title || ''
        title.href = tag.url || '#'
      }
      const viewLink = quickshopEl.querySelector('[data-qs-view]')
      if (viewLink) {
        viewLink.href = tag.url || '#'
        if (tag.tag_id != null) viewLink.dataset.tagId = String(tag.tag_id)
        else delete viewLink.dataset.tagId
        if (tag.id != null) viewLink.dataset.productId = String(tag.id)
        else delete viewLink.dataset.productId
      }

      // Render the option groups. When the pool didn't carry options[] (e.g.
      // a product with a single Default Title variant), we still build a
      // single sentinel group so applySelection() can resolve the variant.
      const optionsContainer = quickshopEl.querySelector('[data-qs-options]')
      const opts = Array.isArray(tag.options) ? tag.options : []
      const meaningful =
        opts.length > 0 && !(opts.length === 1 && opts[0].values?.[0]?.name === 'Default Title')
      qsState.hasOptions = meaningful
      if (optionsContainer) optionsContainer.innerHTML = ''

      const initial = pickInitialVariant(tag)
      qsState.optionValues = optionValuesFromVariant(tag, initial)

      if (meaningful && optionsContainer) {
        for (const opt of opts) {
          const group = document.createElement('div')
          group.className = 'sai-kvb3hsy7__quickshop-group'
          const head = document.createElement('div')
          head.className = 'sai-kvb3hsy7__quickshop-group-head'
          head.innerHTML = `
            <span class="sai-kvb3hsy7__quickshop-group-name">${opt.name}:</span>
            <span class="sai-kvb3hsy7__quickshop-group-selected" data-qs-selected></span>
          `
          group.appendChild(head)

          const pillsRow = document.createElement('div')
          pillsRow.className = 'sai-kvb3hsy7__quickshop-group-pills'
          pillsRow.dataset.qsPills = opt.name
          pillsRow.setAttribute('role', 'radiogroup')
          pillsRow.setAttribute('aria-label', opt.name)
          for (const ov of opt.values || []) {
            pillsRow.appendChild(buildPill(opt, ov, tag))
          }
          group.appendChild(pillsRow)
          optionsContainer.appendChild(group)
        }
      }

      // ATC label baseline (applySelection only flips disabled).
      const atc = quickshopEl.querySelector('[data-action="qs-atc"]')
      if (atc) {
        atc.textContent = settings.ctaText || 'Add to cart'
        atc.dataset.original = settings.ctaText || 'Add to cart'
      }

      applySelection(tag)
    }

    function openQuickshop(tag) {
      if (!quickshopEl || !tag) return
      if (qsCloseTimer) {
        clearTimeout(qsCloseTimer)
        qsCloseTimer = null
      }
      populateQuickshop(tag)
      // Pause video + auto-advance while the sheet is open.
      const active = getActiveVideo()
      if (active) active.pause?.()
      clearAutoTimer()

      quickshopEl.hidden = false
      quickshopEl.setAttribute('aria-hidden', 'false')
      // Force reflow so the transform transition triggers.
      void quickshopEl.offsetHeight
      quickshopEl.classList.add('sai-kvb3hsy7__quickshop--open')
    }

    function closeQuickshop(opts) {
      if (!quickshopEl) return
      if (!quickshopEl.classList.contains('sai-kvb3hsy7__quickshop--open')) return
      quickshopEl.classList.remove('sai-kvb3hsy7__quickshop--open')
      quickshopEl.setAttribute('aria-hidden', 'true')
      qsCloseTimer = setTimeout(() => {
        if (!quickshopEl.classList.contains('sai-kvb3hsy7__quickshop--open')) {
          quickshopEl.hidden = true
        }
        qsCloseTimer = null
      }, 280)
      qsState.variantId = ''
      qsState.handle = ''
      qsState.tag = null
      qsState.optionValues = {}

      // Resume playback unless we're closing because the dialog itself is closing.
      if (!opts || !opts.dialogClosing) {
        const active = getActiveVideo()
        if (active && dialog.open) active.play?.()
      }
    }

    function performAtc(variantId, quantity, btn) {
      const sdk = window.Spectrum?.cart
      const original = btn ? btn.dataset.original || btn.textContent : ''

      // Build payload so add_to_cart and added_to_cart carry an identical
      // shape — funnel assumes 1:1 pairing keyed off the envelope.
      // Direct ATC button (single-variant overlay) lives inside a
      // product card and stamps its own data-tag-id / data-product-id.
      // The mini-PDP qs-atc button lives inside the bottom sheet, which
      // is a sibling of the slide track — no card ancestor — so we fall
      // back to qsState.tag (set by populateQuickshop / openQuickshop).
      const card = btn?.closest?.('.sai-kvb3hsy7__product-card')
      const payload = {
        video_id: _activeVideoHandle(),
        entity_id: _activeVideoHandle() || '',
        entity_type: 'video',
        tag_id: btn?.dataset?.tagId ?? card?.dataset?.tagId ?? qsState.tag?.tag_id,
        product_id: btn?.dataset?.productId ?? card?.dataset?.productId ?? qsState.tag?.id,
        variant_id: variantId,
        quantity: quantity,
      }
      // Intent — fires before the cart call. Mutually exclusive with
      // product_click. Fires regardless of outcome so an add_to_cart without
      // a matching added_to_cart reveals failures.
      _track(`${FEATURE_SLUG}:add_to_cart`, payload)
      _emit(`${FEATURE_SLUG}:add_to_cart`, payload)

      if (btn) {
        btn.disabled = true
        btn.textContent = 'Adding…'
      }

      const finalize = (ok) => {
        if (ok) {
          // Confirmation — fires after cart-add succeeds (track + emit; theme
          // adapters / Klaviyo bridges subscribe here for cart-line side effects).
          _track(`${FEATURE_SLUG}:added_to_cart`, payload)
          _emit(`${FEATURE_SLUG}:added_to_cart`, payload)
          // Close sheet + dialog so the theme's cart-drawer (auto-opened by
          // Spectrum.cart.addAndOpen) can take focus.
          closeQuickshop({ dialogClosing: true })
          close()
        } else if (btn) {
          btn.disabled = false
          btn.textContent = original
        }
      }

      if (sdk && typeof sdk.addAndOpen === 'function') {
        sdk
          .addAndOpen(
            { id: Number(variantId), quantity: quantity },
            { sourceId: `spectrum-${SNIPPET_ID}` },
          )
          .then((res) => finalize(res && res.ok !== false))
          .catch(() => finalize(false))
      } else {
        finalize(false)
      }
    }

    // ── Wiring ──────────────────────────────────────────────────────────
    root.addEventListener('click', (e) => {
      const preview = e.target.closest('.sai-kvb3hsy7__preview')
      if (preview && root.contains(preview)) {
        const handle = preview.dataset.handle
        const idx = handles.indexOf(handle)
        if (idx >= 0) {
          // Analytics: preview_click fires before the dialog opens (track + emit).
          const payload = handle
            ? { video_id: handle, entity_id: handle, entity_type: 'video' }
            : {}
          _track(`${FEATURE_SLUG}:preview_click_elem_clicked`, payload)
          _emit(`${FEATURE_SLUG}:preview_click_elem_clicked`, payload)
          open(idx)
        }
        return
      }
    })

    // Tray-level scroll → list_scroll (debounced 250ms).
    {
      const tray = root.querySelector('.sai-kvb3hsy7__tray')
      if (tray) {
        _lastTrayScrollLeft = tray.scrollLeft
        tray.addEventListener('scroll', _onTrayScroll, { passive: true })
      }
    }

    // pagehide → close + view_duration with exit_reason='pagehide'.
    window.addEventListener('pagehide', () => {
      if (dialog?.open) {
        _pendingExitReason = 'pagehide'
        close()
      }
    })

    dialog.addEventListener('click', (e) => {
      // Pill click — most specific case; handle first. Pills carry
      // option-name + option-value; we update qsState.optionValues then
      // re-resolve everything via applySelection().
      const pill = e.target.closest('.sai-kvb3hsy7__quickshop-pill')
      if (pill && quickshopEl && quickshopEl.contains(pill)) {
        // Out-of-stock pills are inert — visual cues (line-through + dim)
        // already signal it; the click handler hard-blocks selection so the
        // sheet never enters a "no matching variant" state from a tap.
        if (pill.classList.contains('sai-kvb3hsy7__quickshop-pill--oos')) return
        const optName = pill.dataset.optionName
        const optValue = pill.dataset.optionValue
        if (optName && qsState.tag) {
          qsState.optionValues = { ...qsState.optionValues, [optName]: optValue }
          applySelection(qsState.tag)
        }
        return
      }

      const action = e.target.closest('[data-action]')
      if (!action) return
      const kind = action.dataset.action
      if (kind === 'close') close()
      else if (kind === 'mute') setMuted(!state.muted)
      else if (kind === 'prev') goAsset(-1)
      else if (kind === 'next') goAsset(+1)
      else if (kind === 'product-click') {
        // Analytics: title/image/redirect-CTA taps. Mutually exclusive with
        // add_to_cart. Native <a> navigation continues unaffected.
        _fireProductClick(action)
      } else if (kind === 'cta-quickshop') {
        e.preventDefault()
        openQuickshop(getQsTagFromAction(action))
      } else if (kind === 'cta-atc-direct') {
        e.preventDefault()
        const variantId = action.dataset.variantId
        if (variantId) performAtc(variantId, 1, action)
      } else if (kind === 'qs-close') {
        closeQuickshop()
      } else if (kind === 'qs-atc' && !action.disabled) {
        const variantId = qsState.variantId
        if (!variantId) return
        // Defensive — re-resolve against the live variant list to catch the
        // case where a programmatic / keyboard flow flipped state without
        // running through applySelection's availability gate.
        const tag = qsState.tag
        const variant = (tag?.variants || []).find((v) => String(v.id) === String(variantId))
        if (!variant || !variant.available) {
          action.disabled = true
          return
        }
        performAtc(variantId, 1, action)
      }
    })

    dialog.addEventListener('cancel', (e) => {
      e.preventDefault()
      close()
    })

    // Slide interaction — Instagram-style:
    //   • Tap LEFT third  → previous story
    //   • Tap RIGHT third → next story
    //   • Tap MIDDLE      → no-op (video keeps playing)
    //   • Tap and HOLD    → pause; release resumes from same playhead
    //   • Horizontal SWIPE → previous / next story (mobile)
    // Touch events handle hold + swipe; click event handles tap-zone navigation
    // (works on both mobile via post-touch click + desktop mouse). A flag
    // suppresses the click after a swipe / hold so we don't double-fire.
    {
      let startX = 0
      let dx = 0
      let swiping = false
      let holdTimer = null
      let isHolding = false
      let suppressClickNav = false
      const HOLD_DELAY_MS = 180
      const SWIPE_THRESHOLD_FRACTION = 0.18
      const TAP_MOVE_TOLERANCE = 12 // px

      function cancelHoldTimer() {
        if (holdTimer) {
          clearTimeout(holdTimer)
          holdTimer = null
        }
      }

      function resumeFromHold() {
        if (!isHolding) return
        isHolding = false
        // For video assets, restart playback at the existing playhead and
        // re-arm the progress tick. For image assets, re-arm the display
        // timer from scratch — we don't preserve elapsed across hold-pauses
        // because the segment fill is the only signal of "where the timer
        // is" and merchants would rarely notice the second-or-two re-start.
        // Either way startAssetPlayback() picks the right path based on
        // the active asset's data-asset-type.
        const sv = getActiveVideo()
        if (sv) sv.play?.()
        startAssetPlayback()
      }

      slidesEl.addEventListener(
        'touchstart',
        (e) => {
          if (e.touches.length !== 1) return
          swiping = true
          startX = e.touches[0].clientX
          dx = 0
          isHolding = false
          // Don't clearAutoTimer here — only clear if it becomes a swipe or hold.
          // Quick taps (left/right zone navigation) get to keep the auto-advance
          // running until go() resets it for the new slide.
          cancelHoldTimer()
          holdTimer = setTimeout(() => {
            isHolding = true
            clearAutoTimer()
            const sv = getActiveVideo()
            if (sv) sv.pause?.()
          }, HOLD_DELAY_MS)
        },
        { passive: true },
      )

      slidesEl.addEventListener(
        'touchmove',
        (e) => {
          if (!swiping) return
          dx = e.touches[0].clientX - startX
          if (Math.abs(dx) > TAP_MOVE_TOLERANCE) {
            // Confirmed a swipe — cancel any pending hold; auto-advance is
            // about to be reset by the upcoming go() anyway.
            cancelHoldTimer()
            clearAutoTimer()
          }
        },
        { passive: true },
      )

      slidesEl.addEventListener('touchend', () => {
        if (!swiping) return
        swiping = false
        cancelHoldTimer()
        const threshold = window.innerWidth * SWIPE_THRESHOLD_FRACTION

        if (Math.abs(dx) >= threshold) {
          // Swipe — navigate. Suppress the synthesized click that follows.
          suppressClickNav = true
          setTimeout(() => {
            suppressClickNav = false
          }, 350)
          // Horizontal swipe → slide-level nav (not asset-level). Mirrors
          // IG: swipe = "next story", tap-zone = "next thing in the story".
          if (dx < 0) go(state.index + 1, 0)
          else go(state.index - 1, 0)
          isHolding = false
        } else if (isHolding) {
          // Long-press release — resume from current playhead; suppress click.
          suppressClickNav = true
          setTimeout(() => {
            suppressClickNav = false
          }, 350)
          resumeFromHold()
        }
        // Quick tap (no swipe, no hold) → click handler below picks it up.
      })

      slidesEl.addEventListener('touchcancel', () => {
        swiping = false
        cancelHoldTimer()
        if (isHolding) resumeFromHold()
      })

      // Desktop hold-to-pause (mouse). Mirrors the touch path but no swipe —
      // desktop uses click-zones for navigation, not horizontal drag. Skips
      // interactive children so links/buttons keep their native behavior.
      let mouseHolding = false
      slidesEl.addEventListener('mousedown', (e) => {
        if (e.button !== 0) return
        if (e.target.closest('[data-action], .sai-kvb3hsy7__product-card, .sai-kvb3hsy7__cta')) {
          return
        }
        mouseHolding = true
        isHolding = false
        cancelHoldTimer()
        holdTimer = setTimeout(() => {
          isHolding = true
          clearAutoTimer()
          const sv = getActiveVideo()
          if (sv) sv.pause?.()
        }, HOLD_DELAY_MS)
      })

      function endMouseHold() {
        if (!mouseHolding) return
        mouseHolding = false
        cancelHoldTimer()
        if (isHolding) {
          // Hold release — resume + suppress the click event that follows so
          // we don't fire tap-zone navigation on mouseup.
          suppressClickNav = true
          setTimeout(() => {
            suppressClickNav = false
          }, 350)
          resumeFromHold()
        }
      }

      slidesEl.addEventListener('mouseup', endMouseHold)
      // Cursor leaving the slide while held = release. Otherwise the video
      // would stay paused if the user mouses out.
      slidesEl.addEventListener('mouseleave', endMouseHold)

      // Tap-zone navigation — fires on click (mobile post-touch click + desktop
      // mouse). Click-zones: left third → prev, right third → next, middle → no-op.
      slidesEl.addEventListener('click', (e) => {
        if (suppressClickNav) return
        // Skip clicks on interactive elements (product card, CTAs, etc.) so
        // their native behavior (link navigation, ATC) runs unaffected.
        if (e.target.closest('[data-action], .sai-kvb3hsy7__product-card, .sai-kvb3hsy7__cta')) {
          return
        }
        const w = window.innerWidth
        const x = e.clientX
        // Tap-zone navigation cycles assets first (within a multi-asset
        // story) and only advances slides when overflowing the asset list.
        if (x < w * 0.33) goAsset(-1)
        else if (x > w * 0.67) goAsset(+1)
        // Middle third → no-op; let the video keep playing.
      })
    }

    // Pause auto-advance while video is buffering, resume when it can play.
    // `waiting` / `playing` bubble from the inner <video> inside <spectrum-video>
    // — match the event target against getActiveInnerVideo().
    slidesEl.addEventListener('waiting', () => clearAutoTimer(), true)
    slidesEl.addEventListener(
      'playing',
      (e) => {
        const inner = e.target
        if (inner && inner === getActiveInnerVideo()) {
          const ms =
            inner.duration && Number.isFinite(inner.duration) && inner.duration > 0
              ? inner.duration * 1000
              : settings.autoAdvanceSeconds * 1000
          startAutoAdvance(ms)
        }
      },
      true,
    )

    // Page hidden → pause; visible → resume current asset. For video assets
    // we use the spectrum-video methods; for image assets we just need to
    // freeze / re-arm the display timer (startAssetPlayback re-routes by
    // asset type).
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        const sv = getActiveVideo()
        if (sv) sv.pause?.()
        clearAssetTimer()
      } else if (dialog.open) {
        startAssetPlayback()
      }
    })

    return { open, close, hasVideos: handles.length > 0, setAnalytics, applyVariant }
  }

  // Map of outer wrapper container → player. Lets bindAllContainers attach
  // analytics handles to the right player after async __spectrumAi load.
  const _playerByContainer = new WeakMap()

  // ─── Per-instance init ─────────────────────────────────────────────────
  function initInstance(outerContainer) {
    if (outerContainer.dataset.saiQsh181xuInitialized === '1') return
    outerContainer.dataset.saiQsh181xuInitialized = '1'

    const root = outerContainer.querySelector('.sai-kvb3hsy7')
    if (!root) return

    const pool = readSnippetPool(outerContainer)

    // Upgrade preview tray — remove placeholders for missing videos,
    // upgrade the rest with poster + label. Usability is asset-based, not
    // video_url-based: an image-only story has video_url '' but a playable
    // image asset (and its poster_url carries the image). Requiring
    // video_url here would drop every image-only story and, when all
    // stories are image-only, collapse the whole section.
    const previews = root.querySelectorAll('.sai-kvb3hsy7__preview')
    let liveCount = 0
    for (const btn of previews) {
      const handle = btn.dataset.handle
      const video = pool[handle]
      if (!video || getAssets(video).length === 0) {
        btn.remove()
        continue
      }
      upgradePreview(btn, video)
      liveCount += 1
    }

    if (liveCount === 0) {
      // No usable videos — collapse the section entirely.
      root.style.display = 'none'
      return
    }

    const settings = {
      autoAdvanceSeconds: clamp(Number.parseInt(root.dataset.autoAdvanceSeconds, 10) || 5, 1, 30),
      muteOnLaunch: root.dataset.muteOnLaunch !== 'false',
      showProgressBar: root.dataset.showProgressBar !== 'false',
      closeOnLast: root.dataset.closeOnLast !== 'false',
      ctaText: root.dataset.ctaText || 'Shop now',
      ctaAction: root.dataset.ctaAction === 'mini_pdp' ? 'mini_pdp' : 'redirect',
    }

    const player = makePlayer(root, pool, settings)
    _playerByContainer.set(outerContainer, player)
  }

  function bindAllContainers() {
    const snippetApi = window.__spectrumAi?.snippet
    if (!snippetApi || typeof snippetApi.bind !== 'function') return
    const containers = document.querySelectorAll(
      `[data-spectrum-instance-id][data-spectrum-snippet-id="${SNIPPET_ID}"]`,
    )
    for (const node of containers) {
      const player = _playerByContainer.get(node)
      if (!player) continue
      const handles = snippetApi.bind(node, ({ variant, variants, currentVariantId }) => {
        const resolved =
          variant ??
          (Array.isArray(variants) && variants.find((v) => v?.variantId === currentVariantId))
        if (resolved?.content) player.applyVariant(resolved.content)
      })
      if (handles && typeof player.setAnalytics === 'function') {
        player.setAnalytics(handles.track, handles.emit)
      }
    }
  }

  function initAll() {
    const containers = document.querySelectorAll(`[data-spectrum-snippet-id="${SNIPPET_ID}"]`)
    containers.forEach(initInstance)
  }

  // Self-guard pattern — visibility check via mutation_handle is
  // less critical here because the section silently no-ops when no
  // videos resolve. Run on DOMContentLoaded for SSR-rendered shells,
  // and observe future inserts via MutationObserver for late-arriving slots.
  function initAndBind() {
    initAll()
    bindAllContainers()
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAndBind, { once: true })
  } else {
    initAndBind()
  }

  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (!(node instanceof Element)) continue
        if (node.matches?.(`[data-spectrum-snippet-id="${SNIPPET_ID}"]`)) {
          initInstance(node)
        }
        if (node.querySelectorAll) {
          for (const el of node.querySelectorAll(`[data-spectrum-snippet-id="${SNIPPET_ID}"]`)) {
            initInstance(el)
          }
        }
      }
    }
  })
  observer.observe(document.body, { childList: true, subtree: true })
})()
